import { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useEffectOnce } from 'react-use';
import {
  useActiveProjectCohortId,
  useActiveProjectId,
  useNotify,
  useServiceAuth,
} from '../../../hooks';
import SurveyService, {
  AutopopulateRequest,
} from '../../../services/SurveyService';
import Survey, { FieldValueUpdate } from './Survey';

const messages = defineMessages({
  loadError: {
    id: 'useLoadAutopopulateData.loadError',
    defaultMessage: 'An error occurred loading autopopulate data',
  },
});

export type UseLoadAutopopulateDataArgs = {
  survey: Survey;
  patientId?: string;
};

export type UseLoadAutopopulateDataReturnValue = {
  isLoading: boolean;
  valueUpdates: FieldValueUpdate[];
};

const surveyService = new SurveyService();

export default function useLoadAutopopulateData({
  survey,
  patientId,
}: UseLoadAutopopulateDataArgs): UseLoadAutopopulateDataReturnValue {
  const serviceAuth = useServiceAuth();
  const [notify] = useNotify();
  const activeProjectId = useActiveProjectId();
  const activeCohortId = useActiveProjectCohortId();
  const { formatMessage } = useIntl();
  const [isLoading, setIsLoading] = useState(true);
  const [fieldValueUpdates, setFieldValueUpdates] = useState<
    FieldValueUpdate[]
  >([]);
  async function loadData() {
    if (!survey.hasAutopopulationConfigured) return [];

    const request: AutopopulateRequest = {
      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      patientId,
      surveyId: survey.id,
      questions: survey.autopopulationConfig.map(_ => ({
        linkId: _.itemLinkId,
        resourceType: _.autopopulation.resourceType,
        queries: _.autopopulation.queryParams.map(_ => ({
          queryField: _.field,
          queryValue: _.value,
        })),
        valueField: _.autopopulation.valueSelector,
      })),
    };

    const response = await surveyService.generateAutopopulateData(serviceAuth)(
      activeProjectId,
      activeCohortId,
      request,
    );

    return survey.autopopulateResponseToFieldValueUpdates(response.data);
  }

  useEffectOnce(() => {
    setIsLoading(true);
    loadData()
      .then(setFieldValueUpdates)
      .catch(e => {
        console.error(e);
        notify(formatMessage(messages.loadError), 'error');
      })
      .finally(() => setIsLoading(false));
  });

  return { isLoading, valueUpdates: fieldValueUpdates };
}
